import I18n from 'i18n-js';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import React, { useState } from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import * as elog from '@reverbdotcom/commons/src/elog';
import windowWrapper from '@reverbdotcom/commons/src/window_wrapper';
import { RCButtonLink, RCCallout, UtilityVariant } from '@reverbdotcom/cadence/components';
import { ProtectionShieldIcon, NewspaperIcon } from '@reverbdotcom/cadence/icons/react';

import { Paths } from './shared/url_helpers';

const SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID = '5440807127443';

interface INagProps {
  taxType?: string;
}

interface IProps {
  nagId: string;
  nagProps?: INagProps;
}

const TRUST_MESSAGES_NAG = 'trust_messages_nag';
const TRUST_OFFERS_NAG = 'trust_offers_nag';
const INTERNATIONAL_TAX_ID_NAG = 'international_tax_id_nag';

const NAG_ID_TO_PARAMS: {
  [nagId: string]: {
    variant: UtilityVariant;
    icon: React.ComponentProps<typeof RCCallout>['icon'];
    nagComponent: React.ComponentType<INagProps>;
    ctaComponent?: React.ComponentType;
    compact?: boolean;
    requiredProps?: (keyof INagProps)[];
  };
} = {
  [TRUST_MESSAGES_NAG]: {
    variant: 'loud-ultralight',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [TRUST_OFFERS_NAG]: {
    variant: 'loud-ultralight',
    icon: <ProtectionShieldIcon />,
    nagComponent: SuspiciousActivityNag,
  },
  [INTERNATIONAL_TAX_ID_NAG]: {
    variant: 'info-ultralight',
    icon: <NewspaperIcon />,
    nagComponent: InternationalTaxIdNag,
    ctaComponent: InternationalTaxIdCta,
    compact: true,
    requiredProps: ['taxType'],
  },
};

function SuspiciousActivityNag() {
  return (
    <div>
      <p className="mb-0">
        {I18n.t('discovery.dismissibleDashboardBanner.trustMessage')}
      </p>
      <CoreLink
        to={Paths.helpCenterArticlesUrl.expand({ articleId: SUSPICIOUS_ACTIVITY_HC_ARTICLE_ID })}
        className="fg-color-text-primary td-underline"
        target="_blank"
      >
        {I18n.t('discovery.dismissibleDashboardBanner.trustLearnMore')}
      </CoreLink>
    </div>
  );
}

function InternationalTaxIdNag({ taxType = undefined }: INagProps) {
  return (
    <Translate
      tag="span"
      text="discovery.internationalTaxIds.banner.addTaxIdText"
    >
      <strong>
        <Translate
          text="discovery.internationalTaxIds.banner.addTaxIdTitle"
          args={{ taxType: I18n.t(`discovery.internationalTaxIds.banner.${taxType}`) }}
        />
      </strong>
    </Translate>
  );
}

function InternationalTaxIdCta() {
  return (
    <RCButtonLink
      size="small"
      variant="outlined"
      renderLink={c => (
        <CoreLink
          to={Paths.shopTaxPolicy.expand({})}
          target="_blank"
          rel="noopener noreferrer"
        >
          {c}
        </CoreLink>
      )}
    >
      {I18n.t('discovery.internationalTaxIds.banner.buttonText')}
    </RCButtonLink>
  );
}

export function DismissibleDashboardBanner({ nagId, nagProps = {} }: IProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) { return null; }

  async function onDismiss() {
    try {
      await windowWrapper.jQuery.post(Paths.closeNagAlert.expand({ nagId }));
    } catch (e) {
      elog.error('discovery.dashboard_banner_dismiss.error', {}, e);
    }

    setIsDismissed(true);
  }

  // The backend picks nagId, and it can deploy before the frontend does. That means it can
  // ask for a nag this code doesn't have yet, so show nothing instead of crashing.
  const nagParams = NAG_ID_TO_PARAMS[nagId];

  if (!nagParams) { return null; }

  if (nagParams.requiredProps?.some(prop => !nagProps[prop])) { return null; }

  const NagComponent = nagParams.nagComponent;
  const CtaComponent = nagParams.ctaComponent;

  return (
    <div className="mt-2 mb-4">
      <RCCallout
        themeType="utility"
        variant={nagParams.variant}
        border="thin"
        icon={nagParams.icon}
        onDismiss={onDismiss}
        cta={CtaComponent ? <CtaComponent /> : undefined}
        compact={nagParams.compact ? true : undefined}
      >
        <NagComponent
          {...nagProps} // eslint-disable-line react/jsx-props-no-spreading
        />
      </RCCallout>
    </div>
  );
}

export default DismissibleDashboardBanner;
